/**
 * Project Role Change Service v1.0.0
 * 
 * Governed role mutation service for project memberships with:
 * - Dual-mode support (demo in-memory / production Supabase)
 * - Risk-based UX with configurable thresholds
 * - Full audit trail with receipt generation
 * - Client-side risk preview for instant feedback
 * 
 * @module services/ProjectRoleChangeService
 * @see PROJECT_ROLE_CHANGES_SPEC.md
 */

import { supabase } from '@/lib/supabase';
import { IS_DEMO_MODE } from '@/config/env';
import {
  PROJECT_ROLE_CHANGES_SYSTEM_VERSION,
  ProjectRole,
  ProjectRoleRiskLevel,
  ProjectRoleRiskEvaluation,
  ProjectRoleChangeLogEntry,
  ProjectRoleChangeLogEntryWithUsers,
  ChangeProjectRoleRequest,
  ChangeProjectRoleResponse,
  RemoveProjectMemberRequest,
  RemoveMemberResponse,
  RoleChangeHistoryResponse,
  ProjectRoleChangeReceipt,
  ProjectMemberRemovalReceipt,
  evaluateRoleChangeRiskPreview,
} from '@/types/projectRoleChanges';
import { createShowcaseProjects, createShowcaseUsers, SHOWCASE_WORKSPACE_ID } from '@/demo/showcaseCatalog';
import {
  changeDemoProjectParticipantRole,
  createDemoParticipantNotification,
  getDemoProjectParticipant,
  getDemoProjectParticipants,
  resetDemoProjectParticipantStore,
  setDemoProjectParticipantStatus,
} from '@/demo/demoProjectParticipantStore';
import { isProjectAssignmentRole } from '@/types/projectParticipants';
import { assertDemoCanManageProject, setDemoAuthorityActor } from '@/demo/demoAuthoritySession';
import { telemetry } from '@/services/telemetry';

const LABEL_DELIVERY = 'Project Manager';
const LABEL_SUPPLIER = 'Vendor';
const LABEL_CAPITAL = 'Investor';
const LABEL_ALLY = 'Partner';
const UNAVAILABLE_DB_MESSAGE = 'Supabase connection not available';

// =============================================================================
// MODE DETECTION
// =============================================================================

const isDemoMode = IS_DEMO_MODE;

// =============================================================================
// DEMO MODE IN-MEMORY STORAGE
// =============================================================================

interface DemoProjectMember {
  id: string;
  project_id: string;
  user_id: string;
  role: ProjectRole;
  created_at: string;
  updated_at: string;
}

interface DemoProject {
  id: string;
  workspace_id: string;
  name: string;
}

interface DemoUser {
  id: string;
  name: string;
  email: string;
}

// Demo storage maps
let demoLogs: Map<string, ProjectRoleChangeLogEntry> | null = null;
let demoMembers: Map<string, DemoProjectMember> | null = null;
let demoProjects: Map<string, DemoProject> | null = null;
let demoUsers: Map<string, DemoUser> | null = null;
let demoCurrentUserId = '1';

/**
 * Initialize demo mode data structures.
 */
function initDemoData(): void {
  if (demoLogs !== null) return;

  demoLogs = new Map();
  demoMembers = new Map();
  demoProjects = new Map();
  demoUsers = new Map();

  const users: DemoUser[] = createShowcaseUsers().map(({ id, name, email }) => ({ id, name, email }));
  users.forEach((user) => demoUsers!.set(user.id, user));

  const projects: DemoProject[] = createShowcaseProjects().map((project) => ({
    id: project.id,
    workspace_id: SHOWCASE_WORKSPACE_ID,
    name: project.name,
  }));
  projects.forEach((project) => demoProjects!.set(project.id, project));

  const roleMembers: Array<{ user_id: string; role: ProjectRole }> = [
    { user_id: 'cbfc74ff-8622-4227-a560-855e16a45e52', role: LABEL_DELIVERY },
    { user_id: '5', role: LABEL_SUPPLIER },
    { user_id: '8', role: LABEL_CAPITAL },
    { user_id: '532e3ecc-2e17-4be5-a27a-8cd1e47a05a6', role: 'Company' },
    { user_id: '7', role: LABEL_ALLY },
  ];
  const timestamp = '2026-07-20T10:00:00.000Z';
  const members: DemoProjectMember[] = projects.flatMap((project) =>
    roleMembers.map((member) => ({
      id: `showcase-member-${project.id}-${member.user_id}`,
      project_id: project.id,
      user_id: member.user_id,
      role: member.role,
      created_at: timestamp,
      updated_at: timestamp,
    })),
  );
  members.forEach((member) => demoMembers!.set(`${member.project_id}:${member.user_id}`, member));
}

/**
 * Set the current demo user for testing different roles.
 */
export function setDemoCurrentUser(userId: string): void {
  if (isDemoMode) {
    demoCurrentUserId = userId;
    setDemoAuthorityActor(createShowcaseUsers().find(user => user.id === userId) ?? null);
  }
}

/**
 * Reset demo data (for testing).
 */
export function resetDemoData(): void {
  demoLogs = null;
  demoMembers = null;
  demoProjects = null;
  demoUsers = null;
  resetDemoProjectParticipantStore();
  initDemoData();
}

// =============================================================================
// TELEMETRY
// =============================================================================

interface ProjectRoleTelemetryEvent {
  event: 'project_role_changed' | 'project_member_removed' | 'project_role_change_failed';
  project_hash?: string;
  workspace_hash?: string;
  action?: string;
  risk_level?: ProjectRoleRiskLevel;
  error_code?: string;
}

/**
 * Emit telemetry event for project role changes.
 * The authoritative audit record is written by the governed RPC. This
 * best-effort signal is deliberately PII-free and never gates the mutation.
 */
function emitRoleTelemetry(event: ProjectRoleTelemetryEvent): void {
  if (import.meta.env.DEV) {
    console.debug('[ProjectRoleChangeService] Telemetry:', event);
  }
  if (!isDemoMode && telemetry.isEnabled()) {
    const { event: eventName, ...properties } = event;
    telemetry.track(eventName, properties);
  }
}

// =============================================================================
// RISK EVALUATION
// =============================================================================

/**
 * Client-side risk evaluation preview for instant UI feedback.
 * Re-exported from types for convenience.
 */
export { evaluateRoleChangeRiskPreview };

/**
 * Server-side risk evaluation via RPC (production only).
 */
export async function evaluateRoleChangeRiskServer(
  projectId: string,
  targetUserId: string,
  newRole: ProjectRole | null,
  action: 'role_change' | 'remove'
): Promise<ProjectRoleRiskEvaluation | null> {
  if (isDemoMode) {
    // In demo mode, get current membership from demo data and use browser-side evaluation
    const member = getDemoProjectParticipant(projectId, targetUserId);
    return evaluateRoleChangeRiskPreview(
      member?.projectRole || null,
      newRole,
      action
    );
  }

  if (!supabase) return null;
  const currentRole = await getUserProjectRole(projectId, targetUserId);
  if (!currentRole) return null;
  return evaluateRoleChangeRiskPreview(currentRole, newRole, action);
}

// =============================================================================
// CHANGE PROJECT ROLE
// =============================================================================

/**
 * Change a project member's role.
 */
export async function changeProjectMemberRole(
  req: ChangeProjectRoleRequest
): Promise<ChangeProjectRoleResponse> {
  if (isDemoMode) {
    return changeProjectMemberRoleDemo(req);
  }

  if (!supabase) {
    return {
      success: false,
      error: { code: 'UNAUTHORIZED', message: UNAVAILABLE_DB_MESSAGE },
    };
  }

  const { data, error } = await supabase.rpc('change_project_participant_role_secure', {
    p_project_id: req.project_id,
    p_user_id: req.target_user_id,
    p_project_role: req.new_role,
    p_reason: req.justification || null,
  });

  if (error) {
    console.error('[ProjectRoleChangeService] changeProjectMemberRole error:', error);
    emitRoleTelemetry({
      event: 'project_role_change_failed',
      project_hash: req.project_id.slice(0, 8),
      error_code: 'RPC_ERROR',
    });
    return {
      success: false,
      error: { code: 'CAPABILITY_DENIED', message: error.message },
    };
  }

  if (data.success && data.action === 'role_changed') {
    emitRoleTelemetry({
      event: 'project_role_changed',
      project_hash: req.project_id.slice(0, 8),
      action: 'role_changed',
      risk_level: data.risk_level,
    });
  }

  return data as ChangeProjectRoleResponse;
}

/**
 * Demo mode implementation of role change.
 */
async function changeProjectMemberRoleDemo(
  req: ChangeProjectRoleRequest
): Promise<ChangeProjectRoleResponse> {
  initDemoData();

  try {
    assertDemoCanManageProject(req.project_id);
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'CAPABILITY_DENIED',
        message: error instanceof Error ? error.message : 'Project participation management is not allowed.',
      },
    };
  }

  // Validate role
  if (!isProjectAssignmentRole(req.new_role)) {
    return {
      success: false,
      error: { code: 'INVALID_ROLE', message: 'Invalid project role' },
    };
  }

  // Check project exists
  const project = demoProjects!.get(req.project_id);
  if (!project) {
    return {
      success: false,
      error: { code: 'PROJECT_NOT_FOUND', message: 'Project not found' },
    };
  }

  // Check self-change
  if (req.target_user_id === demoCurrentUserId) {
    return {
      success: false,
      error: { code: 'SELF_ROLE_CHANGE', message: 'Cannot change your own project role' },
    };
  }

  // Get current member
  const member = getDemoProjectParticipant(req.project_id, req.target_user_id);
  const currentRole = member?.projectRole || null;

  if (!member || member.status !== 'active') {
    return {
      success: false,
      error: { code: 'CAPABILITY_DENIED', message: 'Project participant was not found' },
    };
  }

  // Idempotent check
  if (currentRole === req.new_role) {
    return {
      success: true,
      action: 'unchanged',
      message: 'User already has this role',
      current_role: currentRole,
      new_role: req.new_role,
    };
  }

  // Compute risk
  const risk = evaluateRoleChangeRiskPreview(currentRole, req.new_role, 'role_change');

  // Check justification for high-risk
  if (risk.level === 'high' && (!req.justification || req.justification.length < 20)) {
    return {
      success: false,
      error: { code: 'JUSTIFICATION_REQUIRED', message: 'High-risk role change requires justification (20+ chars)' },
    };
  }

  // Check justification for medium-risk
  if (risk.level === 'medium' && (!req.justification || req.justification.length < 10)) {
    return {
      success: false,
      error: { code: 'JUSTIFICATION_REQUIRED', message: 'Medium-risk role change requires justification (10+ chars)' },
    };
  }

  // Perform the change
  const now = new Date().toISOString();
  try {
    const changed = changeDemoProjectParticipantRole(req.project_id, req.target_user_id, req.new_role);
    if (changed.action === 'updated') {
      createDemoParticipantNotification({
        userId: req.target_user_id,
        actorId: demoCurrentUserId,
        type: 'project_role_changed',
        title: 'Your project assignment changed',
        body: `Your project assignment is now ${req.new_role}.`,
        projectId: req.project_id,
        companyId: changed.participant.companyId,
        metadata: { oldRole: changed.oldRole, projectRole: req.new_role },
      });
    }
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'CAPABILITY_DENIED',
        message: error instanceof Error ? error.message : 'Could not change project assignment',
      },
    };
  }

  // Create log entry
  const logId = `log-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const logEntry: ProjectRoleChangeLogEntry = {
    id: logId,
    workspace_id: project.workspace_id,
    project_id: req.project_id,
    actor_user_id: demoCurrentUserId,
    target_user_id: req.target_user_id,
    change_type: 'role_change',
    old_role: currentRole,
    new_role: req.new_role,
    risk_level: risk.level,
    risk_reasons: risk.reasons,
    justification: req.justification || null,
    created_at: now,
    receipt_id: `rcpt_prc_${logId.slice(0, 8)}`,
    version: PROJECT_ROLE_CHANGES_SYSTEM_VERSION,
  };
  demoLogs!.set(logId, logEntry);

  emitRoleTelemetry({
    event: 'project_role_changed',
    project_hash: 'demo',
    action: 'role_changed',
    risk_level: risk.level,
  });

  const receipt: ProjectRoleChangeReceipt = {
    receipt_id: logEntry.receipt_id,
    receipt_type: 'project_role_change',
    version: PROJECT_ROLE_CHANGES_SYSTEM_VERSION,
    log_id: logId,
    workspace_id: project.workspace_id,
    project_id: req.project_id,
    target_user_id: req.target_user_id,
    actor_user_id: demoCurrentUserId,
    old_role: currentRole,
    new_role: req.new_role,
    changed_at: now,
    risk_level: risk.level,
  };

  return {
    success: true,
    log_id: logId,
    action: 'role_changed',
    old_role: currentRole,
    new_role: req.new_role,
    risk_level: risk.level,
    risk_reasons: risk.reasons,
    receipt,
  };
}

// =============================================================================
// REMOVE PROJECT MEMBER
// =============================================================================

/**
 * Remove a member from a project.
 */
export async function removeProjectMember(
  req: RemoveProjectMemberRequest
): Promise<RemoveMemberResponse> {
  if (isDemoMode) {
    return removeProjectMemberDemo(req);
  }

  if (!supabase) {
    return {
      success: false,
      error: { code: 'UNAUTHORIZED', message: UNAVAILABLE_DB_MESSAGE },
    };
  }

  const { data, error } = await supabase.rpc('remove_project_member', {
    p_project_id: req.project_id,
    p_target_user_id: req.target_user_id,
    p_justification: req.justification || null,
  });

  if (error) {
    console.error('[ProjectRoleChangeService] removeProjectMember error:', error);
    emitRoleTelemetry({
      event: 'project_role_change_failed',
      project_hash: req.project_id.slice(0, 8),
      error_code: 'RPC_ERROR',
    });
    return {
      success: false,
      error: { code: 'CAPABILITY_DENIED', message: error.message },
    };
  }

  if (data.success && data.action === 'member_removed') {
    emitRoleTelemetry({
      event: 'project_member_removed',
      project_hash: req.project_id.slice(0, 8),
      risk_level: data.risk_level,
    });
  }

  return data as RemoveMemberResponse;
}

/**
 * Demo mode implementation of member removal.
 */
async function removeProjectMemberDemo(
  req: RemoveProjectMemberRequest
): Promise<RemoveMemberResponse> {
  initDemoData();

  try {
    assertDemoCanManageProject(req.project_id);
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'CAPABILITY_DENIED',
        message: error instanceof Error ? error.message : 'Project participation management is not allowed.',
      },
    };
  }

  // Check project exists
  const project = demoProjects!.get(req.project_id);
  if (!project) {
    return {
      success: false,
      error: { code: 'PROJECT_NOT_FOUND', message: 'Project not found' },
    };
  }

  // Check self-removal
  if (req.target_user_id === demoCurrentUserId) {
    return {
      success: false,
      error: { code: 'SELF_REMOVAL', message: 'Cannot remove yourself from project' },
    };
  }

  // Get current member
  const member = getDemoProjectParticipant(req.project_id, req.target_user_id);

  // Idempotent check
  if (!member) {
    return {
      success: true,
      action: 'unchanged',
      message: 'User is not a member of this project',
    };
  }

  const currentRole = member.projectRole;

  // Compute risk
  const risk = evaluateRoleChangeRiskPreview(currentRole, null, 'remove');

  // Check justification for high-risk
  if (risk.level === 'high' && (!req.justification || req.justification.length < 20)) {
    return {
      success: false,
      error: { code: 'JUSTIFICATION_REQUIRED', message: 'High-risk member removal requires justification (20+ chars)' },
    };
  }

  // Check justification for medium-risk
  if (risk.level === 'medium' && (!req.justification || req.justification.length < 10)) {
    return {
      success: false,
      error: { code: 'JUSTIFICATION_REQUIRED', message: 'Medium-risk member removal requires justification (10+ chars)' },
    };
  }

  // Perform the removal
  try {
    setDemoProjectParticipantStatus(req.project_id, req.target_user_id, 'revoked');
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'CAPABILITY_DENIED',
        message: error instanceof Error ? error.message : 'Could not remove project participant',
      },
    };
  }

  // Create log entry
  const now = new Date().toISOString();
  const logId = `log-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const logEntry: ProjectRoleChangeLogEntry = {
    id: logId,
    workspace_id: project.workspace_id,
    project_id: req.project_id,
    actor_user_id: demoCurrentUserId,
    target_user_id: req.target_user_id,
    change_type: 'member_removed',
    old_role: currentRole,
    new_role: null,
    risk_level: risk.level,
    risk_reasons: risk.reasons,
    justification: req.justification || null,
    created_at: now,
    receipt_id: `rcpt_prc_${logId.slice(0, 8)}`,
    version: PROJECT_ROLE_CHANGES_SYSTEM_VERSION,
  };
  demoLogs!.set(logId, logEntry);

  emitRoleTelemetry({
    event: 'project_member_removed',
    project_hash: 'demo',
    risk_level: risk.level,
  });

  const receipt: ProjectMemberRemovalReceipt = {
    receipt_id: logEntry.receipt_id,
    receipt_type: 'project_member_removal',
    version: PROJECT_ROLE_CHANGES_SYSTEM_VERSION,
    log_id: logId,
    workspace_id: project.workspace_id,
    project_id: req.project_id,
    target_user_id: req.target_user_id,
    actor_user_id: demoCurrentUserId,
    removed_role: currentRole,
    removed_at: now,
    risk_level: risk.level,
  };

  return {
    success: true,
    log_id: logId,
    action: 'member_removed',
    removed_role: currentRole,
    risk_level: risk.level,
    risk_reasons: risk.reasons,
    receipt,
  };
}

// =============================================================================
// ROLE CHANGE HISTORY
// =============================================================================

/**
 * Get role change history for a project.
 */
export async function getProjectRoleChangeHistory(
  projectId: string,
  limit = 50,
  offset = 0
): Promise<RoleChangeHistoryResponse | null> {
  if (isDemoMode) {
    return getProjectRoleChangeHistoryDemo(projectId, limit, offset);
  }

  if (!supabase) return null;

  const { data, error, count } = await supabase
    .from('audit_events')
    .select(
      'id, workspace_id, project_id, actor_id, event_type, entity_id, reason, metadata, occurred_at',
      { count: 'exact' },
    )
    .eq('project_id', projectId)
    .in('event_type', ['ProjectRoleChanged', 'ProjectMemberRemoved'])
    .order('occurred_at', { ascending: false })
    .range(offset, offset + Math.max(limit - 1, 0));

  if (error) {
    console.error('[ProjectRoleChangeService] getProjectRoleChangeHistory error:', error);
    return null;
  }

  const membershipIds = [...new Set(
    (data || [])
      .map((event) => event.entity_id)
      .filter((id): id is string => Boolean(id)),
  )];
  const targetUserByMembershipId = new Map<string, string>();
  if (membershipIds.length > 0) {
    const { data: memberships, error: membershipError } = await supabase
      .from('project_memberships')
      .select('id, user_id')
      .eq('project_id', projectId)
      .in('id', membershipIds);
    if (membershipError) {
      console.warn('[ProjectRoleChangeService] role history target lookup failed:', membershipError);
    } else {
      for (const membership of memberships || []) {
        targetUserByMembershipId.set(membership.id, membership.user_id);
      }
    }
  }

  const records: ProjectRoleChangeLogEntryWithUsers[] = (data || []).map((event) => {
    const metadata = event.metadata && typeof event.metadata === 'object' && !Array.isArray(event.metadata)
      ? event.metadata as Record<string, unknown>
      : {};
    const role = typeof metadata.projectRole === 'string'
      ? metadata.projectRole as ProjectRole
      : null;
    const isRemoval = event.event_type === 'ProjectMemberRemoved';
    return {
      id: event.id,
      workspace_id: event.workspace_id,
      project_id: event.project_id ?? projectId,
      actor_user_id: event.actor_id ?? '',
      target_user_id: event.entity_id
        ? targetUserByMembershipId.get(event.entity_id) ?? ''
        : '',
      change_type: isRemoval ? 'member_removed' : 'role_change',
      old_role: isRemoval ? role : null,
      new_role: isRemoval ? null : role,
      risk_level: isRemoval || role === 'Project Manager' ? 'high' : 'medium',
      risk_reasons: [],
      justification: event.reason,
      created_at: event.occurred_at,
      receipt_id: `audit_${event.id}`,
      version: PROJECT_ROLE_CHANGES_SYSTEM_VERSION,
    };
  });

  return {
    success: true,
    records,
    total: count ?? records.length,
    limit,
    offset,
  };
}

/**
 * Demo mode implementation of history query.
 */
async function getProjectRoleChangeHistoryDemo(
  projectId: string,
  limit: number,
  offset: number
): Promise<RoleChangeHistoryResponse> {
  initDemoData();

  const records: ProjectRoleChangeLogEntryWithUsers[] = [];
  for (const [, log] of demoLogs!) {
    if (log.project_id === projectId) {
      records.push({
        ...log,
        actor: demoUsers!.get(log.actor_user_id),
        target: demoUsers!.get(log.target_user_id),
      });
    }
  }

  // Sort by created_at descending
  records.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const total = records.length;
  const paged = records.slice(offset, offset + limit);

  return {
    success: true,
    records: paged,
    total,
    limit,
    offset,
  };
}

// =============================================================================
// PROJECT MEMBER QUERIES
// =============================================================================

/**
 * Get all members of a project (for demo mode / testing).
 */
export async function getProjectMembers(projectId: string): Promise<Array<{
  user_id: string;
  role: ProjectRole;
  user?: DemoUser;
}>> {
  if (isDemoMode) {
    return getDemoProjectParticipants(projectId).map(member => ({
      user_id: member.userId,
      role: member.projectRole,
      user: { id: member.userId, name: member.name, email: member.email ?? '' },
    }));
  }

  if (!supabase) return [];

  // project_memberships is the canonical membership source.
  const { data: pmData } = await supabase
    .from('project_memberships')
    .select(`
      user_id,
      role,
      user:profiles!user_id(id, full_name, email)
    `)
    .eq('project_id', projectId);

  return (pmData || []).map((m) => {
    const userObj = Array.isArray(m.user) ? m.user[0] : m.user;
    return {
      user_id: m.user_id,
      role: m.role as ProjectRole,
      user: userObj ? {
        id: userObj.id,
        name: userObj.full_name,
        email: userObj.email,
      } : undefined,
    };
  });
}

/**
 * Get the current role of a specific user in a project.
 */
export async function getUserProjectRole(
  projectId: string,
  userId: string
): Promise<ProjectRole | null> {
  if (isDemoMode) {
    return getDemoProjectParticipant(projectId, userId)?.projectRole || null;
  }

  if (!supabase) return null;

  // project_memberships is the canonical membership source.
  const { data: pmData } = await supabase
    .from('project_memberships')
    .select('role')
    .eq('project_id', projectId)
    .eq('user_id', userId)
    .single();

  return (pmData?.role as ProjectRole) || null;
}
